import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { DeleteModal } from "CommonComponent";
import { NumericFormat } from "react-number-format";
import isEqual from "lodash/isEqual";

const createRow = (id) => ({
  id,
  heading: "",
  charge: "",
});

const normalizeRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [createRow(1), createRow(2)];
  }

  return rows.map((row, index) => ({
    id: index + 1,
    heading: row?.heading ?? "",
    charge: row?.charge ?? "",
  }));
};

const CurrencyInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => {
      const nextValue = values.value ?? "";
      const currentValue = value ?? "";

      if (String(nextValue) === String(currentValue)) {
        return;
      }

      onChange?.({ target: { value: nextValue } });
    }}
    thousandSeparator
    decimalScale={2}
    allowNegative={false}
    prefix="$ "
    placeholder="$ 0.00"
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
  />
);

const NegativeCurrencyInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => {
      const raw = String(values.value || "").replace(/-/g, "");
      const normalized = raw ? `-${raw}` : "";

      if (String(normalized) === String(value ?? "")) {
        return;
      }

      onChange?.({ target: { value: normalized } });
    }}
    thousandSeparator
    decimalScale={2}
    allowNegative
    prefix="$ "
    placeholder="-$ 0.00"
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
  />
);

const MiscExpensesTab = ({
  selectedSections = [],
  onSectionDataChange,
  initialSectionData = {},
  getFieldError = () => "",
}) => {
  const shouldShow = (sectionId) => selectedSections.includes(sectionId);
  const hasSelectedSection = selectedSections.length > 0;

  const [voiceRecordingConfig, setVoiceRecordingConfig] = useState({
    title: "Professional Voice Recording",
    quoteAmount: "",
    ...(initialSectionData.professional_voice_recording || {}),
  });

  const [miscTelecomRows, setMiscTelecomRows] = useState(
    normalizeRows(initialSectionData.misc_telecom)
  );
  const nextMiscTelecomIdRef = useRef(
    normalizeRows(initialSectionData.misc_telecom).length + 1
  );

  const [miscCreditRows, setMiscCreditRows] = useState(
    normalizeRows(initialSectionData.misc_credit)
  );
  const nextMiscCreditIdRef = useRef(
    normalizeRows(initialSectionData.misc_credit).length + 1
  );
  const oldInitialSectionDataRef = useRef(initialSectionData);
  const isHydratingRef = useRef(false);
  const lastEmittedPayloadRef = useRef(null);
  const onSectionDataChangeRef = useRef(onSectionDataChange);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const toSectionPayload = (voiceConfig, telecomRows, creditRows) => ({
    professional_voice_recording: voiceConfig,
    misc_telecom: telecomRows.map(({ id, ...row }) => row),
    misc_credit: creditRows.map(({ id, ...row }) => row),
  });

  const handleRowFieldChange = (setter, rowId, field) => (event) => {
    const { value } = event.target;
    setter((prev) => {
      let hasChanges = false;

      const nextRows = prev.map((row) => {
        if (row.id !== rowId || row[field] === value) {
          return row;
        }

        hasChanges = true;
        return { ...row, [field]: value };
      });

      return hasChanges ? nextRows : prev;
    });
  };

  const handleRowChargeChange = (setter, rowId) => (event) => {
    const { value } = event.target;
    setter((prev) => {
      let hasChanges = false;

      const nextRows = prev.map((row) => {
        if (row.id !== rowId || row.charge === value) {
          return row;
        }

        hasChanges = true;
        return { ...row, charge: value };
      });

      return hasChanges ? nextRows : prev;
    });
  };

  const handleAddRow = (setter, idRef) => {
    const nextId = idRef.current;
    idRef.current += 1;
    setter((prev) => [...prev, createRow(nextId)]);
  };

  const handleDeleteClick = (type, rowId) => {
    setPendingDelete({ type, rowId });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) {
      handleCloseDeleteModal();
      return;
    }

    if (pendingDelete.type === "telecom") {
      setMiscTelecomRows((prev) =>
        prev.filter((row) => row.id !== pendingDelete.rowId)
      );
    }

    if (pendingDelete.type === "credit") {
      setMiscCreditRows((prev) =>
        prev.filter((row) => row.id !== pendingDelete.rowId)
      );
    }

    handleCloseDeleteModal();
  };

  const getError = (sectionId, field) => getFieldError?.(sectionId, field) || "";

  useEffect(() => {
    onSectionDataChangeRef.current = onSectionDataChange;
  }, [onSectionDataChange]);

  useEffect(() => {
    if (isEqual(oldInitialSectionDataRef.current, initialSectionData)) {
      return;
    }

    isHydratingRef.current = true;

    const nextVoiceConfig = {
      title: "Professional Voice Recording",
      quoteAmount: "",
      ...(initialSectionData.professional_voice_recording || {}),
    };
    const nextMiscTelecomRows = normalizeRows(initialSectionData.misc_telecom);
    const nextMiscCreditRows = normalizeRows(initialSectionData.misc_credit);

    setVoiceRecordingConfig((prev) =>
      isEqual(prev, nextVoiceConfig) ? prev : nextVoiceConfig
    );
    setMiscTelecomRows((prev) =>
      isEqual(prev, nextMiscTelecomRows) ? prev : nextMiscTelecomRows
    );
    setMiscCreditRows((prev) =>
      isEqual(prev, nextMiscCreditRows) ? prev : nextMiscCreditRows
    );

    nextMiscTelecomIdRef.current = nextMiscTelecomRows.length + 1;
    nextMiscCreditIdRef.current = nextMiscCreditRows.length + 1;
    oldInitialSectionDataRef.current = initialSectionData;
  }, [initialSectionData]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    const payload = toSectionPayload(
      voiceRecordingConfig,
      miscTelecomRows,
      miscCreditRows
    );

    if (isEqual(lastEmittedPayloadRef.current, payload)) {
      return;
    }

    const initialPayload = toSectionPayload(
      {
        title: "Professional Voice Recording",
        quoteAmount: "",
        ...(initialSectionData.professional_voice_recording || {}),
      },
      normalizeRows(initialSectionData.misc_telecom),
      normalizeRows(initialSectionData.misc_credit)
    );

    if (isEqual(payload, initialPayload)) {
      return;
    }

    lastEmittedPayloadRef.current = payload;
    onSectionDataChangeRef.current?.(payload);
  }, [voiceRecordingConfig, miscTelecomRows, miscCreditRows, initialSectionData]);

  return (
    <div>
      {!hasSelectedSection && (
        <div className="invoice-section-div mb-0 text-center">
          <span className="font-16 font-weight-bold heading-color">
            First you have to select at least one section.
          </span>
        </div>
      )}

      {shouldShow("professional_voice_recording") && (
      <div className="invoice-section-div">
        <h3 className="d-flex align-items-center gap-2">
          <img src="/assets/images/invoice-user-icon.svg" alt="" />
          <span className="font-16 font-weight-bold heading-color">
            Professional Voice Recording
          </span>
        </h3>
        <div className="bg-light p-3 border-sm mb-3">
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Title"
                  value={voiceRecordingConfig.title || ""}
                  onChange={(event) => {
                    const { value } = event.target;
                    setVoiceRecordingConfig((prev) =>
                      prev.title === value ? prev : { ...prev, title: value }
                    );
                  }}
                  isInvalid={Boolean(getError("professional_voice_recording", "title"))}
                  className="input-field"
                />
                <Form.Control.Feedback type="invalid">{getError("professional_voice_recording", "title")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Quote Amount ($)</Form.Label>
                <CurrencyInput
                  value={voiceRecordingConfig.quoteAmount}
                  onChange={(event) => {
                    const { value } = event.target;
                    setVoiceRecordingConfig((prev) =>
                      prev.quoteAmount === value
                        ? prev
                        : { ...prev, quoteAmount: value }
                    );
                  }}
                  isInvalid={Boolean(getError("professional_voice_recording", "quoteAmount"))}
                />
                <Form.Control.Feedback type="invalid">{getError("professional_voice_recording", "quoteAmount")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>
      )}

      {shouldShow("misc_telecom") && (
      <div className="invoice-section-div">
        <h3 className="d-flex align-items-center gap-2">
          <img src="/assets/images/invoice-user-icon.svg" alt="" />
          <span className="font-16 font-weight-bold heading-color">Misc</span>
        </h3>
        {miscTelecomRows.map((row, rowIndex) => (
          <div className="bg-light p-3 border-sm mb-3" key={row.id}>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Misc Heading</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type here..."
                    value={row.heading}
                    onChange={handleRowFieldChange(
                      setMiscTelecomRows,
                      row.id,
                      "heading"
                    )}
                    isInvalid={Boolean(getError("misc_telecom", "heading", rowIndex))}
                    className="input-field"
                  />
                  <Form.Control.Feedback type="invalid">{getError("misc_telecom", "heading", rowIndex)}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Charge ($)</Form.Label>
                  <CurrencyInput
                    value={row.charge}
                    onChange={handleRowChargeChange(setMiscTelecomRows, row.id)}
                    isInvalid={Boolean(getError("misc_telecom", "charge", rowIndex))}
                  />
                  <Form.Control.Feedback type="invalid">{getError("misc_telecom", "charge", rowIndex)}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={1} className="text-center">
                <button
                  type="button"
                  className="border-0 bg-transparent p-0"
                  onClick={() => handleDeleteClick("telecom", row.id)}
                  aria-label="Delete misc telecom row"
                >
                  <img src="/assets/images/delete-icon.svg" alt="" />
                </button>
              </Col>
            </Row>
          </div>
        ))}
        <button
          type="button"
          className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
          onClick={() => handleAddRow(setMiscTelecomRows, nextMiscTelecomIdRef)}
        >
          <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
        </button>
      </div>
      )}

      {shouldShow("misc_credit") && (
      <div className="invoice-section-div mb-0">
        <h3 className="d-flex align-items-center gap-2">
          <img src="/assets/images/invoice-user-icon.svg" alt="" />
          <span className="font-16 font-weight-bold heading-color">Misc Credit</span>
        </h3>
        {miscCreditRows.map((row, rowIndex) => (
          <div className="bg-light p-3 border-sm mb-3" key={row.id}>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Misc Credit Heading</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type here..."
                    value={row.heading}
                    onChange={handleRowFieldChange(
                      setMiscCreditRows,
                      row.id,
                      "heading"
                    )}
                    isInvalid={Boolean(getError("misc_credit", "heading", rowIndex))}
                    className="input-field"
                  />
                  <Form.Control.Feedback type="invalid">{getError("misc_credit", "heading", rowIndex)}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Charge ($)</Form.Label>
                  <NegativeCurrencyInput
                    value={row.charge}
                    onChange={handleRowChargeChange(setMiscCreditRows, row.id)}
                    isInvalid={Boolean(getError("misc_credit", "charge", rowIndex))}
                  />
                  <Form.Control.Feedback type="invalid">{getError("misc_credit", "charge", rowIndex)}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={1} className="text-center">
                <button
                  type="button"
                  className="border-0 bg-transparent p-0"
                  onClick={() => handleDeleteClick("credit", row.id)}
                  aria-label="Delete misc credit row"
                >
                  <img src="/assets/images/delete-icon.svg" alt="" />
                </button>
              </Col>
            </Row>
          </div>
        ))}
        <button
          type="button"
          className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
          onClick={() => handleAddRow(setMiscCreditRows, nextMiscCreditIdRef)}
        >
          <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
        </button>
      </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onDelete={handleConfirmDelete}
          message="Are you sure you want to delete this row?"
        />
      )}
    </div>
  );
};

export default MiscExpensesTab;